import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { getAggregatedKOTs, generateBill } from "../api/orderApi";
import BillPrintLayout from "../components/BillPrintLayout";
// import axios from "axios"; // fallback placeholder helper or your custom API client instance

export default function BillPage() {
    const { tableId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const restaurantId = user?.restaurantId;
    const dashboardCategory = location.state?.selectedCategory || tableId[0];

    // Billing state data
    const [billData, setBillData] = useState(null);
    const [loading, setLoading] = useState(true);
    const billSubmissionInFlight = useRef(false);
    const [processingBill, setProcessingBill] = useState(null);

    // Optional admin inputs requested
    const [clientName, setClientName] = useState("");
    const [clientMobile, setClientMobile] = useState("");
    const [gstNumber, setGstNumber] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");

    useEffect(() => {
        if (!tableId || !restaurantId) return;

        const fetchBillDetails = async () => {
            try {
                setLoading(true);
                const data = await getAggregatedKOTs(tableId, restaurantId);
                setBillData(data);
            } catch (error) {
                console.error("Failed to load bill items:", error);
                alert("Error fetching billing details.");
            } finally {
                setLoading(false);
            }
        };

        fetchBillDetails();
    }, [tableId, restaurantId]);

    const calculateSubtotal = () => {
        if (!billData?.products) return 0;
        return billData.products.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    };

    const calculateTotalGST = () => {
        if (!billData?.products) return 0;
        return billData.products.reduce((sum, item) => {
            const itemGst = Number(item.gst || item.GSTinPer) || 0;
            return sum + (item.quantity * item.price * (itemGst / 100));
        }, 0);
    };

    const subtotal = calculateSubtotal();
    const totalGst = calculateTotalGST();
    const grandTotal = subtotal + totalGst;

    const handleSaveAndProcessBill = async (shouldPrint) => {
        if (billSubmissionInFlight.current) return;
        billSubmissionInFlight.current = true;
        setProcessingBill(shouldPrint ? "print" : "no-print");

        try {
            const payload = {
                tableId,
                restaurantId,
                clientName: clientName.trim() || "Walk-in Customer",
                gstNumber: gstNumber.trim() || null,
                items: billData?.products,
                subtotal,
                gstTotal: totalGst,
                grandTotal,
                createdBy: user.id,
                kotIdList: billData.kotIdList
            };

            // Trigger New API Endpoint Post Request 
            // await axios.post("/api/bills/finalize", payload);
            const response = await generateBill(payload);
            setInvoiceNumber(response.billId || "----");
            console.log("Bill finalized successfully:", response.billId);
            //alert("Bill finalized and table updated successfully!");

            if (shouldPrint) {
                // Allows state UI to explicitly populate elements before kicking window rendering system
                setTimeout(() => {
                    window.print();
                    //navigate("/dashboard");
                    navigate("/dashboard", { state: { selectedCategory: dashboardCategory } });
                }, 300);
            } else {
                navigate("/dashboard", { state: { selectedCategory: dashboardCategory } });
                //navigate("/dashboard");
            }

        } catch (error) {
            console.error("Error saving bill execution:", error);
            alert("Could not complete bill finalization backend updates.");
            billSubmissionInFlight.current = false;
            setProcessingBill(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <p className="text-gray-600 font-medium">Compiling aggregate bill data...</p>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen py-8 px-4 print:p-0 print:bg-white">
            
            {/* Screen UI View Area Container */}
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden print:hidden">
                
                {/* Header Section */}
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Generate Invoicing</h2>
                        <p className="text-xs text-gray-500">Table allocation: {tableId}</p>
                    </div>
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition"
                    >
                        ← Back to Order
                    </button>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Left Side Options Panel */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-800 tracking-wide uppercase">Admin Modification Customizations</h3>
                        
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Client Name (Optional)
                            </label>
                            <input
                                type="text"
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                placeholder="e.g. John Doe / Company Ltd"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Mobile Number (Optional)
                            </label>
                            <input
                                type="text"
                                value={clientMobile}
                                onChange={(e) => setClientMobile(e.target.value)}
                                placeholder="e.g. 98765 43210"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                GST Number (Optional)
                            </label>
                            <input
                                type="text"
                                value={gstNumber}
                                onChange={(e) => setGstNumber(e.target.value)}
                                placeholder="e.g. 22AAAAA0000A1Z5"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                            />
                        </div>

                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 text-xs text-amber-800">
                            <strong>Note:</strong> Leaving fields empty leaves client values dynamically mapped as default walk-ins without extended business tax structures.
                        </div>
                    </div>

                    {/* Right Side Options Panel */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex flex-col justify-between">
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 mb-3 pb-1 border-b">Order Breakdown</h3>
                            
                            <div className="space-y-2 max-h-48 overflow-y-auto mb-4 pr-1">
                                {billData?.products?.map((item, index) => (
                                    <div key={item.productId || index} className="flex justify-between text-xs text-gray-700">
                                        <span>{item.name} <span className="text-gray-400">× {item.quantity}</span></span>
                                        <span className="font-medium">₹{(item.quantity * item.price).toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t pt-3 space-y-1.5 text-xs text-gray-600">
                                <div className="flex justify-between">
                                    <span>Subtotal:</span>
                                    <span>₹{subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Calculated GST:</span>
                                    <span>₹{totalGst.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between font-bold text-sm text-gray-900 pt-1 border-t border-dashed">
                                    <span>Grand Total:</span>
                                    <span>₹{grandTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2 mt-6">
                            <button
                                onClick={() => handleSaveAndProcessBill(true)}
                                disabled={processingBill !== null}
                                className="w-full bg-green-600 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60 text-white py-3 rounded-lg font-bold text-sm shadow transition-colors duration-150"
                            >
                                {processingBill === "print" ? <><span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white align-[-3px]" />Processing...</> : "Save and Print 💳"}
                            </button>
                            <button
                                onClick={() => handleSaveAndProcessBill(false)}
                                disabled={processingBill !== null}
                                className="w-full bg-orange-600 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60 text-white py-3 rounded-lg font-bold text-sm shadow transition-colors duration-150"
                            >
                                {processingBill === "no-print" ? <><span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white align-[-3px]" />Processing...</> : "Save and Not Print 📁"}
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {/* ========================================================= */}
            {/* HERMETIC THERMAL PRINT TEMPLATE LAYER                    */}
            {/* Hidden on UI Screen View, Displays purely context printing */}
            {/* ========================================================= */}
            <BillPrintLayout 
                data={{
                    tableId,
                    clientName: clientName.trim() || "Walk-In",
                    gstNumber: gstNumber.trim(),
                    products: billData?.products || [],
                    subtotal,
                    totalGst,
                    grandTotal,
                    invoiceNumber
                }}
            />

        </div>
    );
}